<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'

import { ref, computed, onMounted } from 'vue'

import {
  GetBeeNumberSite,
} from '@/stores/SettingsStore.ts'

import { GetConsultantFromLocalStorageOrApi } from '@/services/ConsultantService'
import type { IAoeConsultant } from '@/types/ConsultantTypes'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faFacebook,
  faInstagram,
  faPinterest,
  faYoutube,
  faTiktok,
} from '@fortawesome/free-brands-svg-icons'

const logger = useLogger()

setStoreLogger(logger)

const consultantData = ref<IAoeConsultant | null>(null)
const isLoading = ref<boolean>(true)
const hasError = ref<boolean>(false)

const hasSocialLinks = computed(() => {
  const sm = consultantData.value?.socialMedia
  return sm && (sm.facebook || sm.instagram || sm.pinterest || sm.youTube || sm.tikTok)
})

onMounted(async () => {
  logger.debug('AoeMyStory onMounted')
  await loadMyStory()
})

async function loadMyStory() {
  try {
    isLoading.value = true
    hasError.value = false

    const beeNumberSite = await GetBeeNumberSite()
    if (beeNumberSite !== null) {
      const consultant = await GetConsultantFromLocalStorageOrApi(beeNumberSite)
      if (consultant) {
        consultantData.value = consultant
        logger.debug('My Story loaded successfully', consultant)
      } else {
        logger.warn('No consultant data returned')
      }
    } else {
      logger.error('beeNumberSite is null')
      hasError.value = true
    }
  } catch (error) {
    logger.error('Error loading My Story:', error)
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="aoe-my-story container">
    <div v-if="isLoading" class="loading-container">
      <p>{{ t('labels.Loading') }}...</p>
    </div>

    <div v-else-if="hasError" class="error-container">
      <p>{{ t('common.ErrorLoadingData') }}</p>
    </div>

    <div v-else-if="consultantData" class="story-wrapper">
      <!-- Consultant Info Header -->
      <div class="row p-4 divConsultantDetails">
        <div class="row">
          <div class="col-4">
            <img
              :src="consultantData?.photoUrl"
              :alt="t('cart.ConsultantPhotoAlt')"
              class="payeePhoto">
          </div>
          <div class="col-8">
            <div class="divpayeeName">
              <div class="row">
                <div class="col-12 payeeName">
                  {{ consultantData?.firstName }} {{ consultantData?.lastName }}
                </div>
              </div>
              <div class="row">
                <div class="col-12 payeeLocation">
                  {{ consultantData?.city }}, {{ consultantData?.region }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Story Content -->
      <div v-if="consultantData.storyText" class="story-container">
        <div class="story-content" v-html="consultantData.storyText"></div>
      </div>
      <div v-else class="no-story-container">
        <p>{{ t('myStory.NoStoryAvailable') }}</p>
      </div>
    </div>

    <div v-else class="no-story-container">
      <p>{{ t('myStory.NoStoryAvailable') }}</p>
    </div>

    <!-- Social Media Links -->
    <div v-if="consultantData && hasSocialLinks" class="social-links">
      <a v-if="consultantData.socialMedia?.facebook"
          :href="consultantData.socialMedia.facebook"
          target="_blank"
          rel="noopener noreferrer"
          class="social-link"
          :aria-label="t('consultantCorner.Facebook')">
        <FontAwesomeIcon :icon="faFacebook" class="social-icon" />{{ t('consultantCorner.Facebook') }}
      </a>
      <a v-if="consultantData.socialMedia?.instagram"
          :href="consultantData.socialMedia.instagram"
          target="_blank"
          rel="noopener noreferrer"
          class="social-link"
          :aria-label="t('consultantCorner.Instagram')">
        <FontAwesomeIcon :icon="faInstagram" class="social-icon" />{{ t('consultantCorner.Instagram') }}
      </a>
      <a v-if="consultantData.socialMedia?.pinterest"
          :href="consultantData.socialMedia.pinterest"
          target="_blank"
          rel="noopener noreferrer"
          class="social-link"
          :aria-label="t('consultantCorner.Pinterest')">
        <FontAwesomeIcon :icon="faPinterest" class="social-icon" />{{ t('consultantCorner.Pinterest') }}
      </a>
      <a v-if="consultantData.socialMedia?.youTube"
          :href="consultantData.socialMedia.youTube"
          target="_blank"
          rel="noopener noreferrer"
          class="social-link"
          :aria-label="t('consultantCorner.YouTube')">
        <FontAwesomeIcon :icon="faYoutube" class="social-icon" />{{ t('consultantCorner.YouTube') }}
      </a>
      <a v-if="consultantData.socialMedia?.tikTok"
          :href="consultantData.socialMedia.tikTok"
          target="_blank"
          rel="noopener noreferrer"
          class="social-link"
          :aria-label="t('consultantCorner.TikTok')">
        <FontAwesomeIcon :icon="faTiktok" class="social-icon" />{{ t('consultantCorner.TikTok') }}
      </a>
    </div>

  </div>
</template>

<style lang="scss">
.aoe-my-story {
  color: black;
  padding: $spacing-4;

  .loading-container,
  .error-container,
  .no-story-container {
    text-align: center;
    padding: $spacing-6;
    @include typography-body-large;
  }

  .error-container {
    color: #d32f2f;
  }

  .story-wrapper {
    .divConsultantDetails {
      border-radius: $radius-md;
      border: 2px solid #ead9cf;
      background: #fbf7f5;
      padding: $spacing-3;
      margin-bottom: $spacing-4;
    }

    .payeePhoto {
      width: 100%;
      height: auto;
      border-radius: $radius-md;
    }

    .payeeName {
      @include typography-h4;
      @include font-weight-semibold;
      font-style: normal;
      line-height: 16px;
    }

    .payeeLocation {
      @include typography-body;
    }
  }

  .social-links {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
    padding: $spacing-3 $spacing-4;

    .social-link {
      @include typography-body;
      color: var(--vt-c-teal-dark);
      text-decoration: none;
      padding: $spacing-1 $spacing-3;
      border-radius: $radius-md;
    }

    .social-icon {
      margin-right: $spacing-1;
    }
  }

  .story-container {
    padding: $spacing-4;

    .story-content {
      @include typography-body;
      line-height: 1.6;
      overflow: auto;

      h1, h2, h3, h4, h5, h6 {
        margin-top: $spacing-4;
        margin-bottom: $spacing-3;
        @include font-weight-semibold;
      }

      p {
        margin-bottom: $spacing-3;
      }

      ul, ol {
        margin-bottom: $spacing-3;
        padding-left: $spacing-6;
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: $radius-md;
        margin: $spacing-3 0;
      }

      a {
        color: var(--vt-c-teal-dark);
        text-decoration: underline;

        &:hover {
          color: #0d3d44;
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: $spacing-3;

    .story-wrapper {
      .divConsultantDetails {
        padding: $spacing-2;
      }
    }

    .story-container {
      padding: $spacing-3;
    }
  }
}
</style>
